import { useEffect, useMemo, useState, useCallback } from "react";
import api, { API_BASE, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Target, Calendar, Save, Loader2, CheckCircle2, XCircle,
  TrendingUp, AlertTriangle, Info, FileSpreadsheet, Download,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* helpers                                                              */
/* ------------------------------------------------------------------ */
// Default Total Perjalanan when no manual input exists for a date
const DEFAULT_WEEKDAY_TRIPS = 285; // Monday - Friday
const DEFAULT_WEEKEND_TRIPS = 219; // Saturday - Sunday

// Return default total_perjalanan based on weekday (locale-independent)
function defaultTripsFor(iso) {
  // iso = "YYYY-MM-DD" — parse via Date components to avoid TZ shifts
  const [y, m, d] = iso.split("-").map(Number);
  const day = new Date(y, m - 1, d).getDay(); // 0=Sun ... 6=Sat
  return (day === 0 || day === 6) ? DEFAULT_WEEKEND_TRIPS : DEFAULT_WEEKDAY_TRIPS;
}

function isoToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  return `${v.toFixed(2)}%`;
}
function fmtDateID(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

/* ------------------------------------------------------------------ */
/* small UI atoms                                                       */
/* ------------------------------------------------------------------ */
function PanelTitle({ n, children, right }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center">
        <span className="text-[#93c5fd] mr-2 font-black text-lg">{n}.</span>
        <span className="text-[#1e40af] font-bold text-sm md:text-base tracking-tight uppercase">
          {children}
        </span>
      </div>
      {right}
    </div>
  );
}

function StatCard({ label, value, tone = "slate", sub }) {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    red: "bg-rose-50 border-rose-200 text-rose-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="text-[11px] font-semibold uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black mt-1 tracking-tight">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function AchievementBadge({ pct }) {
  if (pct === null || pct === undefined) {
    return <span className="text-slate-400 text-xs">Belum ada input</span>;
  }
  const ok = pct >= 100;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
        ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
      }`}
      data-testid={ok ? "achievement-met" : "achievement-not-met"}
    >
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {fmtPct(pct)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                            */
/* ------------------------------------------------------------------ */
export default function SPM() {
  const { isAdmin } = useAuth();

  const [startDate, setStartDate] = useState(isoDaysAgo(6));
  const [endDate, setEndDate] = useState(isoToday());
  const [trips, setTrips] = useState({});   // { "YYYY-MM-DD": total_perjalanan }
  const [realisasi, setRealisasi] = useState({}); // { "YYYY-MM-DD": total_realisasi }
  const [saved, setSaved] = useState({});   // dates saved in DB (with {tp, rz})
  const [dirty, setDirty] = useState({}); // { "YYYY-MM-DD": true }
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [calendar, setCalendar] = useState({}); // { "YYYY-MM-DD": {is_weekend_or_holiday, is_holiday, holiday_name, day_name, default_trips} }

  const handleExportXlsx = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("trams_token");
      const url = `${API_BASE}/spm/export?start_date=${startDate}&end_date=${endDate}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Export gagal");
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `SPM_OTW_HARIAN_${startDate}_to_${endDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success("Excel SPM tersimpan");
    } catch (e) {
      toast.error("Gagal export Excel SPM");
    } finally {
      setExporting(false);
    }
  };

  const days = useMemo(() => {
    if (!startDate || !endDate || endDate < startDate) return [];
    // Parse YYYY-MM-DD via components to stay in local time and avoid
    // toISOString() shifting the date backwards for positive-UTC offsets.
    const [sy, sm, sd] = startDate.split("-").map(Number);
    const [ey, em, ed] = endDate.split("-").map(Number);
    const cur = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    const out = [];
    while (cur <= end) {
      const y = cur.getFullYear();
      const m = String(cur.getMonth() + 1).padStart(2, "0");
      const dd = String(cur.getDate()).padStart(2, "0");
      out.push(`${y}-${m}-${dd}`);
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  }, [startDate, endDate]);

  const loadReport = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const [{ data: report }, { data: tripData }, { data: cal }] = await Promise.all([
        api.get("/spm/otp", { params: { start_date: startDate, end_date: endDate } }),
        api.get("/spm/trips", { params: { start_date: startDate, end_date: endDate } }),
        api.get("/spm/calendar", { params: { start_date: startDate, end_date: endDate } }),
      ]);
      setReport(report);
      const calMap = {};
      (cal.items || []).forEach((c) => { calMap[c.date] = c; });
      setCalendar(calMap);
      const savedMap = {};
      (tripData.items || []).forEach((t) => {
        savedMap[t.date] = {
          total_perjalanan: t.total_perjalanan,
          total_realisasi: t.total_realisasi ?? t.total_perjalanan,
        };
      });
      setSaved(savedMap);
      // Prefill inputs using calendar-aware default (holiday counts as weekend)
      const tpMap = {}; const rzMap = {};
      (report.daily || []).forEach((r) => {
        const s = savedMap[r.date];
        const def = calMap[r.date]?.default_trips ?? defaultTripsFor(r.date);
        tpMap[r.date] = s ? s.total_perjalanan : def;
        rzMap[r.date] = s ? s.total_realisasi : def;
      });
      setTrips(tpMap);
      setRealisasi(rzMap);
      setDirty({});
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Gagal memuat data SPM");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleTripChange = (date, raw) => {
    const n = raw === "" ? "" : Math.max(0, parseInt(raw, 10) || 0);
    setTrips((t) => ({ ...t, [date]: n }));
    setDirty((d) => ({ ...d, [date]: true }));
  };

  const handleRealisasiChange = (date, raw) => {
    const n = raw === "" ? "" : Math.max(0, parseInt(raw, 10) || 0);
    setRealisasi((r) => ({ ...r, [date]: n }));
    setDirty((d) => ({ ...d, [date]: true }));
  };

  const saveTrip = async (date) => {
    if (!isAdmin) {
      toast.error("Hanya Admin yang boleh menyimpan Total Perjalanan");
      return;
    }
    const tp = trips[date];
    const rz = realisasi[date];
    if (tp === "" || tp === undefined || tp === null) {
      toast.error("Isi Total Perjalanan terlebih dahulu");
      return;
    }
    setSavingKey(date);
    try {
      await api.post("/spm/trips", {
        date,
        total_perjalanan: Number(tp),
        total_realisasi: rz === "" || rz === undefined || rz === null ? Number(tp) : Number(rz),
      });
      setDirty((d) => { const n = { ...d }; delete n[date]; return n; });
      toast.success(`Tersimpan • ${fmtDateID(date)}`);
      await loadReport();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSavingKey(null);
    }
  };

  const saveAllDirty = async () => {
    const list = Object.keys(dirty);
    if (list.length === 0) return;
    if (!isAdmin) { toast.error("Hanya Admin yang boleh menyimpan Total Perjalanan"); return; }
    setSavingAll(true);
    try {
      for (const d of list) {
        const tp = trips[d];
        const rz = realisasi[d];
        if (tp === "" || tp === undefined || tp === null) continue;
        // eslint-disable-next-line no-await-in-loop
        await api.post("/spm/trips", {
          date: d,
          total_perjalanan: Number(tp),
          total_realisasi: rz === "" || rz === undefined || rz === null ? Number(tp) : Number(rz),
        });
      }
      toast.success(`Tersimpan ${list.length} tanggal`);
      setDirty({});
      await loadReport();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Gagal menyimpan sebagian data");
    } finally {
      setSavingAll(false);
    }
  };

  const dirtyCount = Object.keys(dirty).length;
  const overallOk = report?.sla_met === true;

  return (
    <div className="space-y-6" data-testid="spm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#1e40af]">
            <Target className="w-5 h-5" />
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]">SPM • Dishub</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight mt-1">
            On-Time Performance (OTP)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Standar Pelayanan Minimum • Target ketepatan waktu keberangkatan, henti stasiun, & perjalanan
          </p>
        </div>

        {/* Range picker */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-end gap-3 shadow-sm">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Dari</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="spm-start-date"
              className="h-9 mt-1 text-sm"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Sampai</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="spm-end-date"
              className="h-9 mt-1 text-sm"
            />
          </div>
          <Button
            onClick={loadReport}
            disabled={loading}
            data-testid="spm-refresh-btn"
            className="h-9 bg-[#1e40af] hover:bg-[#1e3a8a] text-white"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
            <span className="ml-1.5">Terapkan</span>
          </Button>
          <Button
            onClick={handleExportXlsx}
            disabled={exporting || loading || days.length === 0}
            data-testid="spm-export-xlsx-btn"
            variant="outline"
            className="h-9 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
            <span className="ml-1.5">Export .xlsx</span>
          </Button>
        </div>
      </div>

      {/* Overview KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Total Perjalanan"
          value={(report?.total_perjalanan || 0).toLocaleString("id-ID")}
          tone="blue"
          sub={`${days.length} hari`}
        />
        <StatCard
          label="Keterlambatan Arrival"
          value={report?.arrival?.delays ?? 0}
          tone="amber"
          sub="Delay > 1 menit"
        />
        <StatCard
          label="Keterlambatan Dwell"
          value={report?.dwell?.delays ?? 0}
          tone="amber"
          sub="Delay > 1 menit"
        />
        <StatCard
          label="Keterlambatan Travel"
          value={report?.travel?.delays ?? 0}
          tone="red"
          sub="Delay > 3 menit"
        />
      </div>

      {/* Total Perjalanan per date input */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <PanelTitle
          n="1"
          right={
            dirtyCount > 0 && (
              <Button
                onClick={saveAllDirty}
                disabled={!isAdmin || savingAll}
                data-testid="spm-save-all-btn"
                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
              >
                {savingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span className="ml-1.5">Simpan Semua ({dirtyCount})</span>
              </Button>
            )
          }
        >
          Input Total Perjalanan Harian
        </PanelTitle>

        <div className="text-xs text-slate-500 mb-3 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#1e40af]" />
          <div>
            Nilai <b>default</b> per hari — <b>Senin – Jumat: {DEFAULT_WEEKDAY_TRIPS}</b>,{" "}
            <b>Sabtu – Minggu: {DEFAULT_WEEKEND_TRIPS}</b>. Kolom <b>Realisasi</b> otomatis mengikuti Total Perjalanan; keduanya dapat diedit manual per tanggal lalu disimpan.
            OTP = ((Total Perjalanan − Jumlah Keterlambatan) / Total Perjalanan) × 100%. TSD = (Total Perjalanan / Total Realisasi) × 100%.
            {!isAdmin && <span className="text-rose-600 font-semibold"> Login sebagai Admin untuk mengedit.</span>}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="spm-trips-table">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 px-3">Tanggal</th>
                <th className="text-left py-2 px-3">Hari</th>
                <th className="text-right py-2 px-3">Total Perjalanan</th>
                <th className="text-right py-2 px-3">Realisasi</th>
                <th className="text-right py-2 px-3">Kejadian</th>
                <th className="text-right py-2 px-3">Arr &gt; 1'</th>
                <th className="text-right py-2 px-3">Dwl &gt; 1'</th>
                <th className="text-right py-2 px-3">Trv &gt; 3'</th>
                <th className="text-right py-2 px-3">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {days.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-slate-400">Pilih rentang tanggal yang valid</td></tr>
              )}
              {days.map((d) => {
                const daily = report?.daily?.find((x) => x.date === d);
                const cal = calendar[d];
                const tpValue = trips[d] ?? (cal?.default_trips ?? defaultTripsFor(d));
                const rzValue = realisasi[d] ?? (cal?.default_trips ?? defaultTripsFor(d));
                const isDirty = !!dirty[d];
                const isSaved = saved[d] !== undefined;
                const dayName = cal?.day_name || (() => {
                  const [yy, mm, dd] = d.split("-").map(Number);
                  return new Date(yy, mm - 1, dd).toLocaleDateString("id-ID", { weekday: "long" });
                })();
                const isHoliday = !!cal?.is_holiday;
                const isWeekend = cal ? cal.is_weekend_or_holiday : (() => {
                  const [yy, mm, dd] = d.split("-").map(Number);
                  return [0, 6].includes(new Date(yy, mm - 1, dd).getDay());
                })();
                return (
                  <tr key={d} className={`border-b border-slate-100 hover:bg-slate-50 ${isHoliday ? "bg-rose-50/40" : ""}`}>
                    <td className="py-2 px-3 font-semibold text-slate-800">{fmtDateID(d)}</td>
                    <td className={`py-2 px-3 text-xs font-medium ${isWeekend ? "text-rose-600" : "text-slate-500"}`}>
                      <div>{dayName}</div>
                      {isHoliday && (
                        <div
                          className="text-[9px] font-bold uppercase tracking-widest text-rose-700 mt-0.5"
                          data-testid={`spm-holiday-tag-${d}`}
                          title={cal?.holiday_name}
                        >
                          Libur • {(cal?.holiday_name || "").slice(0, 20)}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!isSaved && !isDirty && (
                          <span
                            className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-500"
                            data-testid={`spm-default-tag-${d}`}
                            title="Nilai default berdasarkan hari"
                          >
                            Default
                          </span>
                        )}
                        <Input
                          type="number"
                          min="0"
                          value={tpValue}
                          placeholder="0"
                          disabled={!isAdmin}
                          onChange={(e) => handleTripChange(d, e.target.value)}
                          data-testid={`spm-trip-input-${d}`}
                          className={`h-8 text-right w-24 ${
                            isDirty
                              ? "border-amber-400 ring-1 ring-amber-300"
                              : !isSaved
                                ? "text-slate-500 italic"
                                : ""
                          }`}
                        />
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Input
                        type="number"
                        min="0"
                        value={rzValue}
                        placeholder="0"
                        disabled={!isAdmin}
                        onChange={(e) => handleRealisasiChange(d, e.target.value)}
                        data-testid={`spm-realisasi-input-${d}`}
                        className={`h-8 text-right w-24 ml-auto ${
                          isDirty
                            ? "border-amber-400 ring-1 ring-amber-300"
                            : !isSaved
                              ? "text-slate-500 italic"
                              : ""
                        }`}
                      />
                    </td>
                    <td className="py-2 px-3 text-right text-slate-600">{daily?.events ?? 0}</td>
                    <td className="py-2 px-3 text-right text-amber-700 font-semibold">{daily?.arrival_delays ?? 0}</td>
                    <td className="py-2 px-3 text-right text-amber-700 font-semibold">{daily?.dwell_delays ?? 0}</td>
                    <td className="py-2 px-3 text-right text-rose-700 font-semibold">{daily?.travel_delays ?? 0}</td>
                    <td className="py-2 px-3 text-right">
                      <Button
                        onClick={() => saveTrip(d)}
                        disabled={!isAdmin || savingKey === d || !isDirty}
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        data-testid={`spm-save-${d}`}
                      >
                        {savingKey === d ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delay > 1 min table */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <PanelTitle n="2">Tabel Delay &gt; 1 Menit — Arrival &amp; Dwell Time</PanelTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="spm-table-1min">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2.5 px-3">Kategori</th>
                <th className="text-right py-2.5 px-3">Total Perjalanan</th>
                <th className="text-right py-2.5 px-3">Jumlah Keterlambatan</th>
                <th className="text-right py-2.5 px-3">Formula</th>
                <th className="text-right py-2.5 px-3">% Pencapaian</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100" data-testid="row-arrival">
                <td className="py-3 px-3 font-semibold text-slate-800">
                  Arrival Time
                  <div className="text-[11px] font-normal text-slate-500">Delay keberangkatan &gt; 1 menit</div>
                </td>
                <td className="py-3 px-3 text-right">{(report?.total_perjalanan || 0).toLocaleString("id-ID")}</td>
                <td className="py-3 px-3 text-right font-bold text-amber-700">{report?.arrival?.delays ?? 0}</td>
                <td className="py-3 px-3 text-right text-xs text-slate-500">
                  ((T − K) / T) × 100%
                </td>
                <td className="py-3 px-3 text-right">
                  <AchievementBadge pct={report?.arrival?.achievement_pct} />
                </td>
              </tr>
              <tr data-testid="row-dwell">
                <td className="py-3 px-3 font-semibold text-slate-800">
                  Dwell Time
                  <div className="text-[11px] font-normal text-slate-500">Delay henti stasiun &gt; 1 menit</div>
                </td>
                <td className="py-3 px-3 text-right">{(report?.total_perjalanan || 0).toLocaleString("id-ID")}</td>
                <td className="py-3 px-3 text-right font-bold text-amber-700">{report?.dwell?.delays ?? 0}</td>
                <td className="py-3 px-3 text-right text-xs text-slate-500">
                  ((T − K) / T) × 100%
                </td>
                <td className="py-3 px-3 text-right">
                  <AchievementBadge pct={report?.dwell?.achievement_pct} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Delay > 3 min table */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <PanelTitle n="3">Tabel Delay &gt; 3 Menit — Travel Time</PanelTitle>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="spm-table-3min">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2.5 px-3">Kategori</th>
                <th className="text-right py-2.5 px-3">Total Perjalanan</th>
                <th className="text-right py-2.5 px-3">Jumlah Keterlambatan</th>
                <th className="text-right py-2.5 px-3">Formula</th>
                <th className="text-right py-2.5 px-3">% Pencapaian</th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="row-travel">
                <td className="py-3 px-3 font-semibold text-slate-800">
                  Travel Time
                  <div className="text-[11px] font-normal text-slate-500">Delay perjalanan &gt; 3 menit</div>
                </td>
                <td className="py-3 px-3 text-right">{(report?.total_perjalanan || 0).toLocaleString("id-ID")}</td>
                <td className="py-3 px-3 text-right font-bold text-rose-700">{report?.travel?.delays ?? 0}</td>
                <td className="py-3 px-3 text-right text-xs text-slate-500">
                  ((T − K) / T) × 100%
                </td>
                <td className="py-3 px-3 text-right">
                  <AchievementBadge pct={report?.travel?.achievement_pct} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Train Service Delivery */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="spm-tsd-panel">
        <PanelTitle n="4">Train Service Delivery (TSD)</PanelTitle>
        <div className="text-xs text-slate-500 mb-3 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#1e40af]" />
          <div>
            Formula: <b>TSD = (Total Perjalanan / Total Realisasi) × 100%</b>. Target 100%.
            Nilai default Realisasi mengikuti Total Perjalanan; ubah manual di tabel bagian 1 bila realisasi berbeda dari rencana.
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="spm-table-tsd">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2.5 px-3">Indikator</th>
                <th className="text-right py-2.5 px-3">Total Perjalanan</th>
                <th className="text-right py-2.5 px-3">Total Realisasi</th>
                <th className="text-right py-2.5 px-3">Formula</th>
                <th className="text-right py-2.5 px-3">% TSD</th>
              </tr>
            </thead>
            <tbody>
              <tr data-testid="row-tsd">
                <td className="py-3 px-3 font-semibold text-slate-800">
                  Train Service Delivery
                  <div className="text-[11px] font-normal text-slate-500">
                    Rasio jumlah perjalanan rencana terhadap realisasi
                  </div>
                </td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800">
                  {(report?.total_perjalanan || 0).toLocaleString("id-ID")}
                </td>
                <td className="py-3 px-3 text-right font-semibold text-slate-800">
                  {(report?.total_realisasi || 0).toLocaleString("id-ID")}
                </td>
                <td className="py-3 px-3 text-right text-xs text-slate-500">
                  (Total Perjalanan / Total Realisasi) × 100%
                </td>
                <td className="py-3 px-3 text-right">
                  <AchievementBadge pct={report?.train_service_delivery_pct} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Conclusion box */}
      <div
        className={`rounded-2xl border-2 p-6 shadow-sm ${
          overallOk
            ? "bg-emerald-50 border-emerald-300"
            : "bg-rose-50 border-rose-300"
        }`}
        data-testid="spm-conclusion"
      >
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${
              overallOk ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"
            }`}
          >
            {overallOk ? <CheckCircle2 className="w-7 h-7" /> : <AlertTriangle className="w-7 h-7" />}
          </div>
          <div className="flex-1">
            <div className={`text-[11px] font-bold uppercase tracking-[0.2em] ${overallOk ? "text-emerald-700" : "text-rose-700"}`}>
              Kesimpulan SLA — Target 100%
            </div>
            <div className={`text-lg md:text-xl font-black mt-1 ${overallOk ? "text-emerald-800" : "text-rose-800"}`}>
              {overallOk ? "SLA 100% TERCAPAI" : "SLA 100% BELUM TERCAPAI"}
            </div>
            <div className="text-sm text-slate-700 mt-2 leading-relaxed">
              {overallOk ? (
                <>
                  Seluruh indikator (Arrival, Dwell, Travel) tercapai 100% pada periode{" "}
                  <b>{fmtDateID(startDate)} – {fmtDateID(endDate)}</b>. Layanan berjalan sesuai SPM Dishub.
                </>
              ) : (
                <>
                  Terdapat keterlambatan pada periode <b>{fmtDateID(startDate)} – {fmtDateID(endDate)}</b>.
                  Pencapaian terendah: <b>{fmtPct(report?.min_achievement)}</b>.
                  Rincian: Arrival <b>{fmtPct(report?.arrival?.achievement_pct)}</b>,
                  Dwell <b>{fmtPct(report?.dwell?.achievement_pct)}</b>,
                  Travel <b>{fmtPct(report?.travel?.achievement_pct)}</b>.
                </>
              )}
            </div>
            {report?.missing_trip_days && report.missing_trip_days.length > 0 && (
              <div className="mt-2 text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded-lg px-3 py-2 inline-block">
                <Info className="w-3.5 h-3.5 inline-block mr-1 text-[#1e40af]" />
                {report.missing_trip_days.length} tanggal memakai nilai default ({DEFAULT_WEEKDAY_TRIPS}/{DEFAULT_WEEKEND_TRIPS}). Simpan nilai manual bila ingin mengganti.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
